// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC } from "react";

// eslint-disable-next-line spire/fenced-imports
import { css } from "styled-components";

const enum fields {
    headerText = "headerText",
    subHeaderText = "subHeaderText",
    image1Url = "image1Url",
    image2Url = "image2Url",
    image3Url = "image3Url",
    image4Url = "image4Url",
    image5Url = "image5Url",
    image6Url = "image6Url",
    callToActionButtonLink = "callToActionButtonLink",
    callToActionButtonText = "callToActionButtonText",
}

export interface ServiceShowcaseStyles {
    gridContainerStyles: InjectableCss;
    gridItemHeaderImageStyles: InjectableCss;
    serviceContainerStyles: InjectableCss;
    serviceImageContainerStyles: InjectableCss;
    imageTileStyles: InjectableCss;
}

export const serviceShowcaseStyles: ServiceShowcaseStyles = {
    gridContainerStyles: {
        css: css`
            @media print {
                max-width: 100%;
            }
            background: #f1f1f1;
        `,
    },
    gridItemHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
        `,
    },
    imageTileStyles: {
        css: css`
            padding: 0.5em;
        `,
    },
    serviceContainerStyles: {
        css: css`
            margin-top: 1em !important;
        `,
    },
    serviceImageContainerStyles: {
        css: css`
            margin-top: 1em !important;
            margin-left: 0px;
            padding-left: 0px;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headerText]: string;
        [fields.subHeaderText]: string;
        [fields.image1Url]: string;
        [fields.image2Url]: string;
        [fields.image3Url]: string;
        [fields.image4Url]: string;
        [fields.image5Url]: string;
        [fields.image6Url]: string;
        [fields.callToActionButtonLink]: string;
        [fields.callToActionButtonText]: string;
    };
}

type Props = OwnProps & HasHistory;

const styles = serviceShowcaseStyles;
const ServiceShowcaseTiledImages: FC<Props> = ({ fields, history }) => {
    const routeToClick = () => {
        window.location.href = fields.callToActionButtonLink;
    };
    return (
        <StyledWrapper data-test-selector="custom_content_service_showcase">
            <GridContainer
                style={{ paddingTop: "60px", paddingLeft: "60px", paddingBottom: "60px", paddingRight: "0.5em" }}
                {...styles.gridContainerStyles}
            >
                <GridItem width={[0, 0, 0, 1, 1]}></GridItem>
                <GridItem width={[12, 12, 12, 4, 4]} {...styles.serviceContainerStyles}>
                    <GridContainer {...styles.gridItemHeaderImageStyles}>
                        <GridItem width={12} {...styles.gridItemHeaderImageStyles}>
                            <Typography variant="h4">{fields.headerText}</Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.gridItemHeaderImageStyles}>
                            <Typography>{fields.subHeaderText}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <Button size={32} onClick={routeToClick} typographyProps={{ transform: "uppercase" }}>
                                {fields.callToActionButtonText}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem width={[0, 0, 0, 1, 1]}></GridItem>
                <GridItem width={[12, 12, 12, 6, 6]} {...styles.serviceImageContainerStyles}>
                    <GridContainer>
                        {fields.image1Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image1Url} altText={translate("Image 1")} />
                            </GridItem>
                        )}
                        {fields.image2Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image2Url} altText={translate("Image 2")} />
                            </GridItem>
                        )}
                        {fields.image3Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image3Url} altText={translate("Image 3")} />
                            </GridItem>
                        )}
                        {fields.image4Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image4Url} altText={translate("Image 4")} />
                            </GridItem>
                        )}
                        {fields.image5Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image5Url} altText={translate("Image 5")} />
                            </GridItem>
                        )}
                        {fields.image6Url && (
                            <GridItem width={[6, 6, 6, 4, 4]} {...styles.imageTileStyles}>
                                <LazyImage src={fields.image6Url} altText={translate("Image 6")} />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(ServiceShowcaseTiledImages),
    definition: {
        displayName: "Service Showcase Tiled Images",
        icon: "Image",
        fieldDefinitions: [
            {
                name: fields.headerText,
                displayName: "Services header Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The services header text.",
                placeholder: "Services header Text Placeholder",
                isRequired: true,
            },
            {
                name: fields.subHeaderText,
                displayName: "Services subheader text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The services sub-heading text.",
                placeholder: "Sub-heading text placeholder.",
                isRequired: false,
            },
            {
                name: fields.image1Url,
                displayName: "Services Image 1",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.image2Url,
                displayName: "Services Image 2",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.image3Url,
                displayName: "Services Image 3",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.image4Url,
                displayName: "Services Image 4",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.image5Url,
                displayName: "Services Image 5",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.image6Url,
                displayName: "Services Image 6",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.callToActionButtonLink,
                displayName: "Call To Action Button Link",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The URL for the call to action button.",
                placeholder: "",
                isRequired: false,
            },
            {
                name: fields.callToActionButtonText,
                displayName: "Call To Action Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The text to show on the call to action button.",
                placeholder: "",
                isRequired: false,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
